/** UI exposure and deliberate clicks. Successful actions keep their separate existing events. */
const SELECTOR = "[data-poki-ui]";

export function pokiUiPhase(game) {
  const day = Math.max(0, Math.floor(Number(game.currentCycleIndex) || 0)) + 1;
  const state = game.stateMachine?.getState?.() || game.stateMachine?.state;
  return (state === "shake" ? "night-" : state === "fall" ? "day-" : "transition-") + day;
}

export function pokiUiAction(element, game) {
  const action = element?.dataset?.pokiUi;
  if (action === "continue") return game._getContinueBtnMode?.() === "drop_all" ? "drop-all" : "next-day";
  if (action === "pause") return game.isPlayerPaused ? "resume" : "pause";
  return /^[a-z]+(?:-[a-z]+)*$/.test(action || "") ? action : null;
}

export function isPokiUiAvailable(element) {
  if (!element?.isConnected || element.disabled || element.getAttribute("aria-disabled") === "true") return false;
  if (element.closest(".hidden, [hidden], [aria-hidden='true'], [inert]")) return false;
  if (!element.getClientRects().length) return false;
  const win = element.ownerDocument.defaultView;
  const style = win.getComputedStyle(element);
  if (style.visibility === "hidden" || style.display === "none" || style.opacity === "0") return false;
  const r = element.getBoundingClientRect();
  if (r.width <= 0 || r.height <= 0 || r.right <= 0 || r.bottom <= 0 || r.left >= win.innerWidth || r.top >= win.innerHeight) return false;
  // Controls covered by a popup or ad shield are not available.
  const hit = element.ownerDocument.elementFromPoint?.(
    Math.max(0, Math.min(win.innerWidth - 1, r.left + r.width / 2)),
    Math.max(0, Math.min(win.innerHeight - 1, r.top + r.height / 2)));
  return !hit || element.contains(hit);
}

export class PokiUiTracker {
  constructor(root, game) {
    this.root = root;
    this.game = game;
    this.frame = 0;
    this.lastContext = "";
    this.controls = [...root.querySelectorAll(SELECTOR)];
    this.click = event => this.handleClick(event);
    this.onVisibility = () => this.schedule();
    root.addEventListener("click", this.click, true);
    root.addEventListener("transitionend", this.onVisibility, true);
    root.addEventListener("animationend", this.onVisibility, true);
    root.ownerDocument.addEventListener("visibilitychange", this.onVisibility);
    root.ownerDocument.defaultView.addEventListener("resize", this.onVisibility);
    this.observer = new MutationObserver(records => {
      const controlsAdded = records.some(record => record.type === "childList" && [...record.addedNodes].some(
        node => node.nodeType === 1 && (node.matches?.(SELECTOR) || node.querySelector?.(SELECTOR))));
      if (controlsAdded) this.controls = [...root.querySelectorAll(SELECTOR)];
      // Ignore clock hands, counters and animation churn below the controls.
      if (controlsAdded || records.some(record => this.controls.some(
        control => record.target === control || record.target.contains(control)))) this.schedule();
    });
    this.observer.observe(root, { subtree: true, childList: true, attributes: true,
      attributeFilter: ["class", "style", "hidden", "disabled", "aria-hidden", "aria-disabled", "data-poki-ui", "aria-label"] });
    this.schedule();
  }

  syncContext() {
    const context = `${pokiUiPhase(this.game)}:${this.game._getContinueBtnMode?.()}:${!!this.game.isPlayerPaused}`;
    if (context !== this.lastContext) { this.lastContext = context; this.schedule(); }
  }

  schedule() {
    if (this.frame) return;
    this.frame = this.root.ownerDocument.defaultView.requestAnimationFrame(() => { this.frame = 0; this.scan(); });
  }

  record(element, interact = false) {
    const tracker = this.game._getPokiEngagement?.();
    const action = pokiUiAction(element, this.game);
    if (!tracker || !action || this.root.ownerDocument.hidden || !isPokiUiAvailable(element)) return;
    for (const suffix of ["all", pokiUiPhase(this.game)]) {
      const what = action + "-" + suffix;
      tracker.visible("ui-button", what);
      if (interact) tracker.emit("ui-button", what, "interact");
    }
  }

  scan() {
    const tracker = this.game._getPokiEngagement?.();
    if (!tracker || this.root.ownerDocument.hidden) return;
    const phase = pokiUiPhase(this.game);
    for (const control of this.controls) {
      const action = pokiUiAction(control, this.game);
      if (action && !tracker.seen.has("ui-button:" + action + "-" + phase)) this.record(control);
    }
  }

  handleClick(event) {
    // Portrait controls forward .click() to desktop handlers. Ignore those
    // synthetic proxies; a real pointer or keyboard activation is counted once.
    if (!event.isTrusted) return;
    const element = event.target?.closest?.(SELECTOR);
    if (element && this.root.contains(element)) this.record(element, true);
  }

  destroy() {
    const doc = this.root.ownerDocument;
    this.observer.disconnect();
    doc.defaultView.cancelAnimationFrame(this.frame);
    this.root.removeEventListener("click", this.click, true);
    this.root.removeEventListener("transitionend", this.onVisibility, true);
    this.root.removeEventListener("animationend", this.onVisibility, true);
    doc.removeEventListener("visibilitychange", this.onVisibility);
    doc.defaultView.removeEventListener("resize", this.onVisibility);
  }
}

export function installPokiUiTracking(root, game) {
  if (!root || !game._getPokiEngagement?.()) return null;
  if (!game.pokiUiTracker) game.pokiUiTracker = new PokiUiTracker(root, game);
  return game.pokiUiTracker;
}
